import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, collection, onSnapshot } from 'firebase/firestore';
import {
  ONETIME_ACHIEVEMENTS,
  getAchievementId,
  getAchievementCategories,
  getRankIndex,
  getTotalAchievements,
} from '../constants/achievements';
import { RANKS, BASE_ELO } from '../constants/ranks';

function AchievementsPage({ user, userData, players, isMobile, seasonConfig, embedded }) {
  const [achievements, setAchievements] = useState(new Set());
  const [achievementDocs, setAchievementDocs] = useState({});
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  const claimedPlayerId = userData?.claimedPlayer;

  useEffect(() => {
    if (!user || !claimedPlayerId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    // Real-time listener for player progress
    const unsubPlayer = onSnapshot(doc(db, 'players', claimedPlayerId), (snap) => {
      if (snap.exists()) {
        setProgress(snap.data().achievementProgress || {});
      }
      setLoading(false);
    });

    // Real-time listener for earned achievements
    const unsubAch = onSnapshot(collection(db, 'players', claimedPlayerId, 'achievements'), (snap) => {
      const earned = new Set();
      const docs = {};
      snap.docs.forEach(d => {
        earned.add(d.id);
        docs[d.id] = d.data();
      });
      setAchievements(earned);
      setAchievementDocs(docs);
    });

    return () => {
      unsubPlayer();
      unsubAch();
    };
  }, [user, claimedPlayerId]);

  const wrapperClass = embedded ? 'achievements-embedded' : 'achievements-page';

  if (!user || !userData) {
    return (
      <div className={wrapperClass}>
        <div className="achievements-login-prompt">
          <h2>Achievements</h2>
          <p>Log in to view your achievements.</p>
        </div>
      </div>
    );
  }

  if (!claimedPlayerId) {
    return (
      <div className={wrapperClass}>
        <div className="achievements-login-prompt">
          <h2>Achievements</h2>
          <p>Claim a player on the Ranks page to track achievements.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={wrapperClass}>
        <div className="achievements-loading">Loading achievements...</div>
      </div>
    );
  }

  const categories = getAchievementCategories();
  const totalAchievements = getTotalAchievements();
  const earnedCount = achievements.size;

  return (
    <div className={wrapperClass}>
      <div className="achievements-header">
        <h2>Achievements</h2>
        <div className="achievements-summary">
          <span className="achievements-count">{earnedCount} / {totalAchievements}</span>
          <div className="achievements-total-bar">
            <div
              className="achievements-total-bar-fill"
              style={{ width: `${(earnedCount / totalAchievements) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {Object.entries(categories).map(([catKey, cat]) => (
        <AchievementCategory
          key={catKey}
          catKey={catKey}
          cat={cat}
          progress={progress}
          achievements={achievements}
          achievementDocs={achievementDocs}
        />
      ))}

      {(
        <div className="achievement-category">
          <div className="achievement-category-header">
            <span className="achievement-category-icon">🏆</span>
            <span className="achievement-category-label">Feats</span>
            <span className="achievement-category-count">
              {ONETIME_ACHIEVEMENTS.filter(a => achievements.has(a.id)).length}/{ONETIME_ACHIEVEMENTS.length}
            </span>
          </div>
          <div className="achievement-grid">
            {ONETIME_ACHIEVEMENTS.map((ach) => {
              const earned = achievements.has(ach.id);
              return (
                <div
                  key={ach.id}
                  className={`achievement-badge ${earned ? 'earned' : 'locked'} onetime`}
                  title={earned ? `${ach.label} — ${ach.description}` : ach.description}
                >
                  <div className="achievement-badge-icon">
                    {earned ? ach.icon : '🔒'}
                  </div>
                  <div className="achievement-badge-milestone">{ach.label}</div>
                  <div className="achievement-badge-desc">{ach.description}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function AchievementCategory({ catKey, cat, progress, achievements, achievementDocs }) {
  const isRank = cat.type === 'rank';
  const milestones = cat.milestones;
  const rewards = cat.rewards;
  const tokenIcon = cat.tokenType === 'crewmate' ? '🔵' : '🔴';

  // Calculate current progress value
  let currentVal = 0;
  if (isRank) {
    const elo = progress[cat.eloField] || BASE_ELO;
    currentVal = getRankIndex(elo);
  } else {
    currentVal = progress[cat.statField] || 0;
  }

  // Find next unearned milestone
  let nextMilestoneIdx = -1;
  for (let i = 0; i < milestones.length; i++) {
    const achId = getAchievementId(catKey, milestones[i]);
    if (!achievements.has(achId)) {
      nextMilestoneIdx = i;
      break;
    }
  }

  // Progress bar calculation
  let progressPercent = 100;
  let progressLabel = 'Complete!';
  if (nextMilestoneIdx >= 0) {
    if (isRank) {
      const targetIdx = nextMilestoneIdx;
      const prevIdx = nextMilestoneIdx > 0 ? nextMilestoneIdx - 1 : 0;
      const prevThreshold = RANKS[prevIdx]?.min || 0;
      const targetThreshold = RANKS[targetIdx]?.min || 0;
      const elo = progress[cat.eloField] || BASE_ELO;
      if (targetThreshold > prevThreshold) {
        progressPercent = Math.min(100, ((elo - prevThreshold) / (targetThreshold - prevThreshold)) * 100);
      }
      progressLabel = `${RANKS[targetIdx]?.name || '???'}`;
    } else {
      const target = milestones[nextMilestoneIdx];
      const prev = nextMilestoneIdx > 0 ? milestones[nextMilestoneIdx - 1] : 0;
      if (target > prev) {
        progressPercent = Math.min(100, ((currentVal - prev) / (target - prev)) * 100);
      }
      progressLabel = `${currentVal} / ${target}`;
    }
  }

  const earnedInCategory = milestones.filter((m) =>
    achievements.has(getAchievementId(catKey, m))
  ).length;

  return (
    <div className="achievement-category">
      <div className="achievement-category-header">
        <span className="achievement-category-icon">{cat.icon}</span>
        <span className="achievement-category-label">{cat.label}</span>
        <span className="achievement-category-count">{earnedInCategory}/{milestones.length}</span>
      </div>

      {nextMilestoneIdx >= 0 && (
        <div className="achievement-progress-section">
          <div className="achievement-progress-bar">
            <div
              className={`achievement-progress-bar-fill ${cat.tokenType}`}
              style={{ width: `${Math.max(2, progressPercent)}%` }}
            />
          </div>
          <span className="achievement-progress-label">{progressLabel}</span>
        </div>
      )}

      <div className="achievement-grid">
        {milestones.map((milestone, i) => {
          const achId = getAchievementId(catKey, milestone);
          const earned = achievements.has(achId);
          const reward = rewards[i];
          const displayMilestone = isRank ? milestone : milestone.toLocaleString();

          return (
            <div
              key={achId}
              className={`achievement-badge ${earned ? 'earned' : 'locked'} ${cat.tokenType}`}
              title={earned ? `Earned! +${reward}${tokenIcon}` : `Reach ${displayMilestone}`}
            >
              <div className="achievement-badge-icon">
                {earned ? cat.icon : '🔒'}
              </div>
              <div className="achievement-badge-milestone">{displayMilestone}</div>
              {reward > 0 && (
                <div className={`achievement-badge-reward ${earned ? 'earned' : ''}`}>
                  +{reward}{tokenIcon}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { AchievementCategory };
export default AchievementsPage;
